import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import AdmittedPatientsModal from "../admission/AdmittedPatientsModal";
import CommonButton from "../../common11/Buttons/CommonButton";
import PrintButton from "../../common11/Buttons/PrintButton";
import CommonModal from "../../common11/commonModals/CommonModal";
import CommonDatePicker from "../../common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../../common11/formInputs/CommonTimePicker";
import InputField from "../../common11/formInputs/InputField";
import RadioButtonGroup from "../../common11/formInputs/RadioButtonGroup";
import SearchDropdown from "../../common11/formInputs/SearchDropdown";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../common11/toastUtils";
import { IpdPatientInfoCard } from "../shared/IpdPatientSearchPanel";
import { getDefaultDateTime } from "../shared/ipdPatientUtils";
import {
  listAdmittedPatientsForDoctorRound,
  mapFormToSavePayload,
  mapListRowForTable,
  mapRecordToFormValues,
  mapWebPatientToAdmissionRow,
} from "../../../services/tprService";
import {
  clearEditing,
  fetchTprById,
  fetchTprList,
  fetchTprReport,
  saveTpr,
  setPatient,
} from "../../../store/slices/tprSlice";

const SUGAR_TYPE_OPTIONS = [
  { label: "F/PP", value: "fpp" },
  { label: "Random", value: "random" },
];

const VITALS_LIST_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "bp", label: "BP" },
  { key: "pulse", label: "Pulse" },
  { key: "temp", label: "Temp." },
  { key: "resp", label: "Resp." },
  { key: "addedBy", label: "Added By" },
];

const mapAdmissionRowToPatient = (row) => ({
  uhid: row.uhid || "",
  patientName: row.patientName || "",
  contact: row.contact || "",
  patientGender: row.gender || "",
  patientAge: row.age || "",
  doctorName: row.doctorName || "",
  doctorId: row.doctorId != null ? Number(row.doctorId) : null,
  wardName: row.wardName || "",
  bedNo: row.bedNo || "",
  admissionId: Number(row.admissionId) || 0,
  regId: row.regId != null ? Number(row.regId) : null,
});

function TPRScreen() {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const { patient, records, editingTprId, loading, saving, printing } =
    useSelector((state) => state.tpr);

  const { date: defaultDate, time: defaultTime } = getDefaultDateTime();
  const [listModalOpen, setListModalOpen] = useState(false);
  const [patientsModalOpen, setPatientsModalOpen] = useState(false);
  const [admissionRows, setAdmissionRows] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const defaultValues = {
    patientSearch: null,
    addedBy: "",
    date: defaultDate,
    time: defaultTime,
    bp: "",
    resp: "",
    pulse: "",
    temp: "",
    sugarType: "fpp",
    preMeal: "",
    postMeal: "",
    randomSugar: "",
    spo2: "",
    o2: "",
    pcv: "",
    platelet: "",
    weight: "",
    ag: "",
    otherObs: "",
    insulin: "",
  };

  const { control, handleSubmit, reset, setValue } = useForm({ defaultValues });
  const formBusy = loading || saving || printing;

  const tableRows = useMemo(
    () => (records || []).map(mapListRowForTable),
    [records]
  );

  const loadIpPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const data = await listAdmittedPatientsForDoctorRound({
        isDischarged: false,
      });
      setAdmissionRows((data || []).map(mapWebPatientToAdmissionRow));
    } catch (error) {
      setAdmissionRows([]);
      showErrorToast(error.message || "Failed to load admitted patients");
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIpPatients();
  }, [loadIpPatients]);

  useEffect(() => {
    if (authUser?.userName) {
      setValue("addedBy", authUser.userName);
    }
  }, [authUser?.userName, setValue]);

  const patientSearchOptions = useMemo(
    () =>
      admissionRows
        .filter((row) => row.status === "Admitted")
        .map((row) => ({
          id: row.admissionId,
          label: [
            row.patientName,
            row.uhid,
            row.age ? `${row.age}Y` : null,
            row.doctorName,
          ]
            .filter(Boolean)
            .join(" | "),
          value: row.patientName,
          row,
        })),
    [admissionRows]
  );

  const loadRecordsForPatient = useCallback(
    async (regId, admissionId) => {
      if (!regId || !admissionId) return false;
      const result = await dispatch(fetchTprList({ regId, admissionId }));
      if (fetchTprList.rejected.match(result)) {
        showErrorToast(result.payload || "Failed to load TPR records");
        return false;
      }
      return true;
    },
    [dispatch]
  );

  const handleSelectPatient = (row) => {
    if (!row) {
      dispatch(setPatient(null));
      setValue("patientSearch", null);
      return;
    }
    const mapped = mapAdmissionRowToPatient(row);
    dispatch(setPatient(mapped));
    setValue("patientSearch", {
      id: mapped.admissionId || mapped.uhid,
      label: mapped.patientName || "",
      value: mapped.contact || mapped.uhid || "",
    });
    dispatch(clearEditing());
    if (mapped.regId && mapped.admissionId) {
      loadRecordsForPatient(mapped.regId, mapped.admissionId);
    }
  };

  const handlePatientSearchSelect = (selectedOption) => {
    if (!selectedOption) {
      handleSelectPatient(null);
      return;
    }
    if (selectedOption.row) {
      handleSelectPatient(selectedOption.row);
    }
  };

  const handleReset = () => {
    const { date, time } = getDefaultDateTime();
    reset({
      ...defaultValues,
      date,
      time,
      addedBy: authUser?.userName || "",
      patientSearch: patient
        ? {
            id: patient.admissionId || patient.uhid,
            label: patient.patientName,
            value: patient.contact || patient.uhid,
          }
        : null,
    });
    dispatch(clearEditing());
  };

  const handleOpenList = async () => {
    if (!patient?.admissionId || !patient?.regId) {
      showWarningToast("Please select a patient first");
      return;
    }
    setListModalOpen(true);
    await loadRecordsForPatient(patient.regId, patient.admissionId);
  };

  const handleEditRow = async (row) => {
    if (!row?.tprId || formBusy) return;
    const result = await dispatch(fetchTprById(row.tprId));
    if (fetchTprById.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load TPR record");
      return;
    }
    const formValues = mapRecordToFormValues(result.payload);
    if (!formValues) return;

    Object.entries(formValues).forEach(([key, value]) => {
      setValue(key, value);
    });
    setValue("addedBy", formValues.addedBy || authUser?.userName || "");
    setListModalOpen(false);
    showSuccessToast("Record loaded for edit");
  };

  const handlePrint = async () => {
    if (!patient?.admissionId) {
      showWarningToast("Please select a patient first");
      return;
    }
    const result = await dispatch(fetchTprReport(patient.admissionId));
    if (fetchTprReport.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load TPR report");
      return;
    }
    const rows = result.payload || [];
    showSuccessToast(
      rows.length
        ? `TPR report loaded (${rows.length} row${rows.length === 1 ? "" : "s"})`
        : "No TPR records to print for this admission"
    );
  };

  const handleSave = handleSubmit(async (data) => {
    if (formBusy) return;
    if (!patient?.patientName) {
      showWarningToast("Please select a patient first");
      return;
    }
    if (!patient.admissionId || !patient.regId) {
      showWarningToast("Selected patient is missing admission or registration id");
      return;
    }

    const userId = Number(authUser?.userId || 1);
    const payload = mapFormToSavePayload(data, patient, userId, {
      isUpdate: Boolean(editingTprId),
    });

    const result = await dispatch(
      saveTpr({
        tprId: editingTprId,
        payload,
      })
    );

    if (saveTpr.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to save TPR vitals");
      return;
    }

    showSuccessToast(
      editingTprId
        ? "TPR vitals updated successfully"
        : "TPR vitals saved successfully"
    );
    handleReset();
    await loadRecordsForPatient(patient.regId, patient.admissionId);
  });

  const tprFormSection = (
    <div className="rounded border border-gray-200 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <InputField
          name="addedBy"
          control={control}
          label="Added By"
          fontSize="text-xs"
          isFloatingLabel
          disabled
        />
        <CommonDatePicker
          name="date"
          control={control}
          label="Date"
          type="date"
          fontSize="text-xs"
          isFloatingLabel
        />
        <CommonTimePicker
          name="time"
          control={control}
          label="Time"
          fontSize="text-xs"
          isFloatingLabel
          use24Hour={false}
          format="hh:mm A"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InputField name="bp" control={control} label="BP" fontSize="text-xs" isFloatingLabel />
        <InputField name="resp" control={control} label="Resp." fontSize="text-xs" isFloatingLabel />
        <InputField name="pulse" control={control} label="Pulse" fontSize="text-xs" isFloatingLabel />
        <InputField name="temp" control={control} label="Temp." fontSize="text-xs" isFloatingLabel />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
        <div className="flex flex-col gap-1">
          <RadioButtonGroup
            name="sugarType"
            control={control}
            label="Sugar"
            options={SUGAR_TYPE_OPTIONS}
            optionLabelClassName="text-xs"
            className="min-h-[2.5rem]"
          />
        </div>
        <InputField
          name="preMeal"
          control={control}
          label="Pre Meal"
          fontSize="text-xs"
          isFloatingLabel
        />
        <InputField
          name="postMeal"
          control={control}
          label="Post Meal"
          fontSize="text-xs"
          isFloatingLabel
        />
        <InputField
          name="randomSugar"
          control={control}
          label="Random"
          fontSize="text-xs"
          isFloatingLabel
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InputField name="spo2" control={control} label="SpO2" fontSize="text-xs" isFloatingLabel />
        <InputField name="o2" control={control} label="O2" fontSize="text-xs" isFloatingLabel />
        <InputField name="pcv" control={control} label="PCV" fontSize="text-xs" isFloatingLabel />
        <InputField
          name="platelet"
          control={control}
          label="Platlete"
          fontSize="text-xs"
          isFloatingLabel
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InputField name="weight" control={control} label="Weight" fontSize="text-xs" isFloatingLabel />
        <InputField name="ag" control={control} label="AG" fontSize="text-xs" isFloatingLabel />
        <InputField
          name="otherObs"
          control={control}
          label="Oth Obs."
          fontSize="text-xs"
          isFloatingLabel
        />
        <InputField
          name="insulin"
          control={control}
          label="Insuline"
          fontSize="text-xs"
          isFloatingLabel
        />
      </div>
    </div>
  );

  const vitalsListSection = (
    <div className="rounded border border-gray-300">
      <div className="overflow-auto max-h-[60vh]">
        <table className="min-w-full text-[11px]">
          <thead className="bg-teal-700 text-white sticky top-0">
            <tr>
              {VITALS_LIST_COLUMNS.map((col) => (
                <th key={col.key} className="px-2 py-1.5 text-left font-semibold">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={VITALS_LIST_COLUMNS.length}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  Loading records…
                </td>
              </tr>
            ) : tableRows.length === 0 ? (
              <tr>
                <td
                  colSpan={VITALS_LIST_COLUMNS.length}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  No vitals records.
                </td>
              </tr>
            ) : (
              tableRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-gray-200 hover:bg-teal-50 cursor-pointer"
                  onClick={() => handleEditRow(row)}
                >
                  {VITALS_LIST_COLUMNS.map((col) => (
                    <td key={col.key} className="px-2 py-1.5 text-gray-700">
                      {row[col.key] || "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-2 sm:p-3 pb-24">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="sticky top-0 z-10 rounded-t-lg px-3 py-2 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-[#217E90]">
            Temperature - Pulse - Respiration
          </h2>
        </div>

        <div className="p-3 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div className="w-full sm:w-[40%]">
              <SearchDropdown
                name="patientSearch"
                control={control}
                label="Search by patient name or mobile number"
                dataArray={patientSearchOptions}
                placeholder="Search by patient name or UHID"
                isSearchable
                isClearable
                fontSize="text-xs sm:text-sm"
                isFloatingLabel
                onChange={handlePatientSearchSelect}
              />
            </div>
            <div className="flex-shrink-0">
              <CommonButton
                label="Admitted Patients"
                type="button"
                size="sm"
                className="bg-[#217E90] text-white hover:bg-[#1a5e70] px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto"
                onClick={() => setPatientsModalOpen(true)}
                disabled={patientsLoading}
              />
            </div>
          </div>
          <IpdPatientInfoCard patient={patient} />
          {patient && tprFormSection}
        </div>
      </div>

      <AdmittedPatientsModal
        isOpen={patientsModalOpen}
        onClose={() => setPatientsModalOpen(false)}
        onSelectPatient={handleSelectPatient}
        onRowDoubleClick={handleSelectPatient}
        rows={admissionRows}
      />

      <CommonModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        title="Vitals List"
        size="lg"
        maxHeightVh={90}
        paperSx={{
          width: { xs: "calc(100vw - 16px)", lg: "min(90vw, 960px)" },
          maxWidth: { lg: "min(90vw, 960px)" },
        }}
        disableBackdropClose={false}
        showCloseButton
        actions={[]}
      >
        {vitalsListSection}
      </CommonModal>

      <div className="fixed bottom-4 right-4 z-50 flex flex-wrap justify-end gap-2 sm:gap-3 pr-2 sm:pr-6">
        <CommonButton
          label="Vitals List"
          onClick={handleOpenList}
          variant="reset-outlined"
          size="sm"
          type="button"
          className="px-3 md:px-5 text-xs md:text-sm rounded-full bg-emerald-50 text-emerald-700 border border-emerald-400 hover:bg-emerald-100"
        />
        <PrintButton
          label={printing ? "Loading…" : "Print TPR"}
          onClick={handlePrint}
          size="sm"
          type="button"
          disabled={formBusy}
          className="px-3 md:px-5 text-xs md:text-sm"
        />
        <CommonButton
          label="Reset"
          onClick={handleReset}
          variant="danger-outline"
          size="sm"
          type="button"
          disabled={formBusy}
          className="px-3 md:px-5 text-xs md:text-sm"
        />
        <CommonButton
          label={saving ? "Saving…" : editingTprId ? "Update" : "Save"}
          onClick={handleSave}
          variant="primary-filled"
          size="sm"
          type="button"
          disabled={formBusy}
        />
      </div>
    </div>
  );
}

export default TPRScreen;
